import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import copyToClipboard from 'copy-to-clipboard';
import { connect } from 'react-redux';
import Icon, { Size } from '@jetbrains/ring-ui-built/components/icon';
import CheckmarkIcon from '@jetbrains/icons/checkmark';
import InfoIcon from '@jetbrains/icons/info';
import ArrowIcon from '@jetbrains/icons/arrow-right';
import LinkIcon from '@jetbrains/icons/link';
import LearnIcon from '@jetbrains/icons/learn';
import CopyIcon from '@jetbrains/icons/copy';
import { Tooltip } from '@rescui/tooltip';

import { makeAlert, trim } from '../services/helper';
import ANALYTIC_CATEGORIES from '../constants/analyticCategories';
import { SHOW_ALERT } from '../redux/actionTypes';
import { trackEvent } from '../constants/analytics';

import './styles/final-step.css';
import { useCardCn } from '@rescui/card';
import cn from 'classnames';
import { useTextStyles } from '@rescui/typography';

const FinalStep = ({
  generatedProject,
  templateLink,
  onCopySuccess,
  theme,
}) => {
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  useEffect(() => {
    if (showCopiedMessage) {
      const timer = setTimeout(() => setShowCopiedMessage(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopiedMessage]);

  const downloadUrl = useMemo(
      () => window.URL.createObjectURL(generatedProject.blob || new Blob()),
      [generatedProject],
  );

  const onLinkClick = useCallback(
      (linkKey) => trackEvent(ANALYTIC_CATEGORIES.LINK, linkKey),
      [],
  );

  const navigateToDocumentation = useCallback(
      () => onLinkClick('navigate-to-documentation'),
      [onLinkClick],
  );

  const copyLinkToProjectTemplate = useCallback(
      (evt) => {
        evt.preventDefault();
        copyToClipboard(templateLink);
        setShowCopiedMessage(true);
        onCopySuccess('Link to current project template is copied!');
        return onLinkClick('copy-link-to-template');
      },
      [onCopySuccess, onLinkClick, templateLink],
  );

  const downloadByLink = useCallback(
      () => onLinkClick('download-by-direct-link'),
      [onLinkClick],
  );

  const textCn = useTextStyles();
  const cardCn = useCardCn();

  return (
    <div className="final-step">
      <section className={cn(cardCn(), 'final-step__download-blurb')}>
        <div className="final-step__header">
          <Icon
            glyph={CheckmarkIcon}
            color={Icon.Color.GREEN}
            size={Size.Size24}
            className="final-step__success-icon"
            aria-hidden="true"
          />
          <div>
            <h2 className={textCn('rs-h2')}>{'Your project is ready to go!'}</h2>
            <p className={textCn('rs-text-2', { hardness: 'hard' })}>
              {'Your project '}
              <strong>{trim(generatedProject.fileName)}</strong>
              {' download should start shortly.'}
            </p>
            <div className="final-step__download-notice">
              <Icon glyph={InfoIcon} size={Size.Size16} aria-hidden="true" />
              <span className={textCn('rs-text-2', { hardness: 'hard' })}>
                {'If the download doesn\'t start, use the '}
                <a
                  className="rs-link"
                  href={downloadUrl}
                  onClick={downloadByLink}
                  download={generatedProject.fileName}
                >
                  {'direct download link'}
                </a>
                {'.'}
              </span>
            </div>
          </div>
        </div>

        <div className="final-step__separator" />
        <div className="final-step__next">
          <h3 className={textCn('rs-h3')}>Next steps</h3>
          <a
            href="https://ktor.io/docs/server-create-a-new-project.html#unpacking"
            className="final-step__tutorial-link"
            target="_blank"
            rel="noreferrer"
            onClick={navigateToDocumentation}
          >
            <Icon
              glyph={LearnIcon}
              color={Icon.Color.GRAY}
              size={Size.Size32}
              aria-hidden="true"
            />
            <span className={textCn('rs-text-2', { hardness: 'hard' })}>
              Learn how to open and run your Ktor project
            </span>
            <Icon
              glyph={ArrowIcon}
              color={Icon.Color.GRAY}
              size={Size.Size18}
              className="final-step__arrow"
              aria-hidden="true"
            />
          </a>

          <Tooltip content="Copied to clipboard!" isVisible={showCopiedMessage}>
            <button
              type="button"
              className="final-step__template-link"
              onClick={copyLinkToProjectTemplate}
            >
              <span className="final-step__template-content">
                <Icon glyph={LinkIcon} color={Icon.Color.GRAY} size={Size.Size24} />
                <span className={textCn('rs-text-2', { hardness: 'hard' })}>
                  <span>Copy link to template</span>
                  <span className="final-step__template-description">
                    Share or recreate this project configuration
                  </span>
                </span>
              </span>
              <Icon
                glyph={CopyIcon}
                color={Icon.Color.GRAY}
                size={Size.Size18}
                aria-hidden="true"
              />
            </button>
          </Tooltip>
        </div>
      </section>
    </div>
  );
};

FinalStep.propTypes = {
  generatedProject: PropTypes.object,
  templateLink: PropTypes.string,
  onCopySuccess: PropTypes.func,
  theme: PropTypes.string,
};

const mapStateToProps = ({
  projectConfig,
  generatedProject,
  serializationModel,
  theme,
}) => ({
  templateLink: `${window.location.protocol}//${
    window.location.host
  }/settings?${serializationModel.toSearchString(projectConfig)}`,
  generatedProject,
  theme,
});

const mapDispatchToProps = (dispatch) => ({
  onCopySuccess: (message) =>
    dispatch({
      type: SHOW_ALERT,
      payload: { alert: makeAlert(message) },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FinalStep);
